interface TableState {
  filters: Record<string, string[]>;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  searchQuery: string;
  columnOrder?: string[];
}

interface AppSettings {
  infoTablePageSize: number;
  imagesTablePageSize: number;
  infoTablePageIndex: number;
  imagesTablePageIndex: number;
  theme: 'light' | 'dark';
  currentBookListId: string | null;
  selectedList: string | null; // null, 'ALL', or list filename
  bookListView: 'info' | 'pages';
  approvalPanelPosition: { x: number; y: number } | null;
  spreadEditorCollapsed: boolean;
  spreadEditorLeftPageCollapsed: boolean;
  spreadEditorRightPageCollapsed: boolean;
  pagesTableEnlargeOnHover: boolean;
  // Table state for restoring filters, pagination, and search
  infoTableState: TableState | null;
  pagesTableState: TableState | null;
}

const DEFAULT_SETTINGS: AppSettings = {
  infoTablePageSize: 25,
  imagesTablePageSize: 10,
  infoTablePageIndex: 0,
  imagesTablePageIndex: 0,
  theme: 'light',
  currentBookListId: null,
  selectedList: null,
  bookListView: 'info',
  approvalPanelPosition: null,
  spreadEditorCollapsed: false,
  spreadEditorLeftPageCollapsed: false,
  spreadEditorRightPageCollapsed: false,
  pagesTableEnlargeOnHover: true,
  infoTableState: null,
  pagesTableState: null,
};

const STORAGE_KEY = 'book-admin-settings';

class SettingsService {
  private settings: AppSettings = { ...DEFAULT_SETTINGS };

  constructor() {
    this.loadSettings();
  }

  private loadSettings(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedSettings = JSON.parse(stored);
        // Merge with defaults to handle new settings that might be missing
        this.settings = { ...DEFAULT_SETTINGS, ...parsedSettings };
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      this.settings = { ...DEFAULT_SETTINGS };
    }
  }

  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }

  getInfoTablePageSize(): number {
    return this.settings.infoTablePageSize;
  }

  setInfoTablePageSize(size: number): void {
    this.settings.infoTablePageSize = size;
    this.saveSettings();
  }

  getImagesTablePageSize(): number {
    return this.settings.imagesTablePageSize;
  }

  setImagesTablePageSize(size: number): void {
    this.settings.imagesTablePageSize = size;
    this.saveSettings();
  }

  getInfoTablePageIndex(): number {
    return this.settings.infoTablePageIndex;
  }

  setInfoTablePageIndex(index: number): void {
    this.settings.infoTablePageIndex = index;
    this.saveSettings();
  }

  getImagesTablePageIndex(): number {
    return this.settings.imagesTablePageIndex;
  }

  setImagesTablePageIndex(index: number): void {
    this.settings.imagesTablePageIndex = index;
    this.saveSettings();
  }

  getTheme(): 'light' | 'dark' {
    return this.settings.theme;
  }

  setTheme(theme: 'light' | 'dark'): void {
    this.settings.theme = theme;
    this.saveSettings();
  }

  getCurrentBookListId(): string | null {
    return this.settings.currentBookListId;
  }

  setCurrentBookListId(id: string | null): void {
    this.settings.currentBookListId = id;
    this.saveSettings();
  }

  getAllSettings(): AppSettings {
    return { ...this.settings };
  }

  resetToDefaults(): void {
    this.settings = { ...DEFAULT_SETTINGS };
    this.saveSettings();
  }

  getApprovalPanelPosition(): { x: number; y: number } | null {
    return this.settings.approvalPanelPosition;
  }

  setApprovalPanelPosition(position: { x: number; y: number }): void {
    this.settings.approvalPanelPosition = position;
    this.saveSettings();
  }

  getSpreadEditorCollapsed(): boolean {
    return this.settings.spreadEditorCollapsed ?? false;
  }

  setSpreadEditorCollapsed(collapsed: boolean): void {
    this.settings.spreadEditorCollapsed = collapsed;
    this.saveSettings();
  }

  getSpreadEditorLeftPageCollapsed(): boolean {
    return this.settings.spreadEditorLeftPageCollapsed ?? false;
  }

  setSpreadEditorLeftPageCollapsed(collapsed: boolean): void {
    this.settings.spreadEditorLeftPageCollapsed = collapsed;
    this.saveSettings();
  }

  getSpreadEditorRightPageCollapsed(): boolean {
    return this.settings.spreadEditorRightPageCollapsed ?? false;
  }

  setSpreadEditorRightPageCollapsed(collapsed: boolean): void {
    this.settings.spreadEditorRightPageCollapsed = collapsed;
    this.saveSettings();
  }

  getBookListView(): 'info' | 'pages' {
    return this.settings.bookListView ?? 'info';
  }

  setBookListView(view: 'info' | 'pages'): void {
    this.settings.bookListView = view;
    this.saveSettings();
  }

  getPagesTableEnlargeOnHover(): boolean {
    return this.settings.pagesTableEnlargeOnHover ?? true;
  }

  setPagesTableEnlargeOnHover(enlarge: boolean): void {
    this.settings.pagesTableEnlargeOnHover = enlarge;
    this.saveSettings();
  }

  // Info table state management
  getInfoTableState(): TableState | null {
    return this.settings.infoTableState ? { ...this.settings.infoTableState } : null;
  }

  setInfoTableState(state: TableState): void {
    this.settings.infoTableState = {
      filters: { ...state.filters },
      pagination: { ...state.pagination },
      searchQuery: state.searchQuery,
      columnOrder: state.columnOrder,
    };
    this.saveSettings();
  }

  // Pages table state management
  getPagesTableState(): TableState | null {
    return this.settings.pagesTableState ? { ...this.settings.pagesTableState } : null;
  }

  setPagesTableState(state: TableState): void {
    this.settings.pagesTableState = {
      filters: { ...state.filters },
      pagination: { ...state.pagination },
      searchQuery: state.searchQuery,
      columnOrder: state.columnOrder,
    };
    this.saveSettings();
  }

  getSelectedList(): string | null {
    return this.settings.selectedList ?? null;
  }

  setSelectedList(list: string | null): void {
    this.settings.selectedList = list;
    this.saveSettings();
  }
}

// Export singleton instance
export const settingsService = new SettingsService();
export type { AppSettings, TableState };
